// [PURPOSE]: Authentication context for Firebase Auth integration
// [WHY]: Provides currentUser, isAuthenticated, isPremium state to entire app
// [INTEGRATION]: Wraps all other providers in AppProviders.js
// [CREATED]: v1.0 Track 2A — Firebase Auth setup

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { auth } from '../firebase/config';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  EmailAuthProvider,
  signInWithPopup,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  updateProfile,
  deleteUser,
  linkWithPopup,
  unlink
} from 'firebase/auth';
import { ref, get, set, remove } from 'firebase/database';
import { APP_VERSION } from '../constants/version';
import { database } from '../firebase/config';
// [S6]: Analytics tracking for auth and purchase events
import { trackEvent, identifyUser } from '../services/analytics';
import { reportError } from '../services/errorReporter';
// [BATCH 1.5 — S-12]: Shared helper for clearing user-scoped localStorage on logout / delete.
import { clearUserScopedStorage } from '../utils/userStorageCleanup';
// [BATCH 13b / P1 D-33 — APR 2026]: AuthContext sits above CharacterContext
//   in the provider tree so it can't import logActivity directly. We emit
//   account events on the systemBus; CharacterContext subscribes and writes
//   to the active character's activity log (Rule 13).
import { systemBus, EVENTS } from '../services/systemBus';

// ========================================
// CONTEXT
// ========================================

const AuthContext = createContext(null);

/**
 * [PURPOSE]: Custom hook to access auth context
 * [WHY]: Cleaner than importing AuthContext directly
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // [GRACEFUL]: Return safe defaults if AuthProvider not mounted yet
    return {
      currentUser: null,
      isAuthenticated: false,
      isPremium: false,
      aiTokens: 0,
      loading: true,
      requiresConsent: false,
      login: async () => {},
      signup: async () => {},
      loginWithGoogle: async () => {},
      logout: async () => {},
      refreshPremiumStatus: async () => {},
      acceptTerms: async () => {},
      exportAllUserData: async () => {},
      deleteAccount: async () => {},
      reauthenticate: async () => {},
      linkGoogleAccount: async () => ({ success: false, error: 'Not authenticated' }),
      unlinkGoogleAccount: async () => ({ success: false, error: 'Not authenticated' }),
    };
  }
  return context;
};

// ========================================
// PROVIDER
// ========================================

/**
 * [PURPOSE]: Authentication provider wrapping entire app
 * [WHY]: Firebase Auth state needs to be available everywhere
 * [INTEGRATION]:
 *   - Uses firebase/config.js for auth instance
 *   - Reads premium status from Firebase Realtime Database
 *   - Consumed by AuthModal, PremiumGate, AccountPanel, AIImageGenerator
 */
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [premiumStatus, setPremiumStatus] = useState({
    isPremium: false,
    aiTokens: 0,
    purchaseDate: null,
    totalImagesGenerated: 0,
  });
  // [S5]: Track whether authenticated user needs to accept Terms of Service
  const [requiresConsent, setRequiresConsent] = useState(false);

  // ========================================
  // AUTH STATE LISTENER
  // ========================================

  useEffect(() => {
    // [GUARD]: If Firebase auth is not configured, skip listener
    if (!auth) {
      console.warn('⚠️ Firebase Auth not configured — running in offline mode');
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // Fetch premium status from Firebase DB
        await fetchPremiumStatus(user.uid);
      } else {
        // Reset premium status on logout
        setPremiumStatus({
          isPremium: false,
          aiTokens: 0,
          purchaseDate: null,
          totalImagesGenerated: 0,
        });
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // [FIX C-2 + APR18-FIX]: Auto-refresh premium status after Stripe checkout redirect
  // [WHY]: After payment, Stripe redirects back with ?purchase=success or ?tokens=success.
  //        Webhook may take 1–5 seconds to update Firebase. Previous logic did ONE retry at
  //        3s via fire-and-forget setTimeout — often missed the webhook window and left the
  //        UI showing isPremium=false on the paying user's account until manual refresh.
  // [FIX]: Poll Firebase up to 5 times at 3s intervals (15s total), stop as soon as
  //        hasPremiumLicense flips to true.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchaseSuccess = params.get('purchase') === 'success';
    const tokensSuccess = params.get('tokens') === 'success';

    if ((purchaseSuccess || tokensSuccess) && currentUser?.uid) {
      // [S6]: Track successful purchase redirect return (Stripe checkout completed)
      if (purchaseSuccess) trackEvent('premium_purchased', { price: '3.99', currency: 'usd' });
      if (tokensSuccess) trackEvent('tokens_purchased');

      // Clean URL params immediately
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);

      let cancelled = false;
      const pollForPremium = async () => {
        const maxAttempts = 5;
        for (let i = 0; i < maxAttempts; i++) {
          if (cancelled) return;
          const result = await fetchPremiumStatus(currentUser.uid);
          // For purchase flow: stop polling once premium is seen.
          // For tokens flow: stop after ~2 attempts (webhook just adds tokens, no state flip).
          if (purchaseSuccess && result?.isPremium) return;
          if (tokensSuccess && i >= 1) return;
          if (i < maxAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, 3000));
          }
        }
      };

      pollForPremium();
      return () => { cancelled = true; };
    }
  }, [currentUser]);

  // ========================================
  // PREMIUM STATUS
  // ========================================

  /**
   * [PURPOSE]: Fetch premium/token status from Firebase DB
   * [WHY]: Premium status persists across platforms via Firebase
   * [SCHEMA]: users/{uid}/hasPremiumLicense, aiTokens, etc.
   */
  const fetchPremiumStatus = async (uid) => {
    // [BATCH 13a / P1 D-26 — APR 2026]: Stripped 13 verbose
    //   console.log/console.error diagnostics (originally added Apr 21
    //   2026 to debug a premium-gate issue). Some logged
    //   `userData.val()` raw — leaked UID, email, premium fields.
    //   The original gate bug was diagnosed; logs lingered into ship
    //   builds. Errors still surface via thrown rejection / return
    //   null; callers handle the failure path.
    if (!database) {
      return null;
    }

    try {
      const userRef = ref(database, `users/${uid}`);
      const snapshot = await get(userRef);

      if (snapshot.exists()) {
        const userData = snapshot.val();

        // [APR 21 2026 FIX]: Case-insensitive premium flag lookup. Firebase
        // Console accepts any casing when fields are manually added, and at
        // least one test account has the field stored as 'haspremiumlicense'
        // (all lowercase). JavaScript field access is case-sensitive, so a
        // direct read of userData.hasPremiumLicense returns undefined. Scan
        // all keys case-insensitively to catch every variant.
        const findKeyCI = (obj, target) => {
          const t = target.toLowerCase();
          return Object.keys(obj || {}).find(k => k.toLowerCase() === t);
        };
        const premiumKey = findKeyCI(userData, 'hasPremiumLicense')
          || findKeyCI(userData, 'isPremium')
          || findKeyCI(userData, 'premium');
        const premiumRaw = premiumKey ? userData[premiumKey] : undefined;
        const premiumFlag = premiumRaw === true
          || premiumRaw === 'true'
          || premiumRaw === 1;

        const status = {
          isPremium: premiumFlag,
          aiTokens: userData.aiTokens || 0,
          purchaseDate: userData.purchaseDate || null,
          totalImagesGenerated: userData.totalImagesGenerated || 0,
        };
        setPremiumStatus(status);
        identifyUser({ isPremium: premiumFlag });
        setRequiresConsent(!userData.consent?.terms);
        return status;
      } else {
        // [APR 21 2026 FIX, REVISED 2026-05-03 BATCH 1]: No doc at users/{uid}
        // = user is free-tier. Do NOT attempt to create one client-side —
        // premium-related fields (`hasPremiumLicense`, `isPremium`, `aiTokens`,
        // `purchaseDate`, `consumedTokens`, `totalImagesGenerated`) carry
        // `.validate: false` clauses in database.rules.json so the client
        // CANNOT forge them. Only the Stripe webhook (admin SDK, bypasses
        // rules) writes those fields. Client only writes `users/{uid}/consent`
        // (validated for required keys). See database.rules.json + Phase 2
        // audit (S-1) for the full rule shape.
        const freeStatus = {
          isPremium: false,
          aiTokens: 0,
          purchaseDate: null,
          totalImagesGenerated: 0,
        };
        setPremiumStatus(freeStatus);
        return freeStatus;
      }
    } catch (error) {
      // [BATCH 13a / D-26]: errors still bubble — caller can decide
      //   whether to surface a toast. Avoid leaking error.code or
      //   userData details into production console.
      reportError(error, { area: 'load_premium_status' });
      return null;
    }
  };

  /**
   * [PURPOSE]: Refresh premium status (called after purchase or by manual Settings button)
   * [WHY]: Stripe webhook updates DB asynchronously; UI needs to re-fetch.
   * [APR18-FIX]: Returns the fetched status so the Settings "Refresh Premium" button can
   *              show a toast ("Premium active" / "Still no premium found — contact support").
   */
  const refreshPremiumStatus = async () => {
    if (!currentUser?.uid) return null;
    return fetchPremiumStatus(currentUser.uid);
  };

  // ========================================
  // AUTH METHODS
  // ========================================

  /**
   * [PURPOSE]: Email/password login
   * @param {string} email
   * @param {string} password
   * @returns {Object} { success: boolean, error?: string }
   */
  const login = async (email, password) => {
    if (!auth) return { success: false, error: 'Firebase not configured' };

    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      // [S6]: Track successful email login (no PII — no email address)
      trackEvent('sign_in', { method: 'email' });
      // [BATCH 13b / P1 D-33 — APR 2026]: Rule 13 — emit so the
      //   character activity log captures the sign-in.
      systemBus.emit(EVENTS.SIGNED_IN, {
        uid: credential?.user?.uid,
        provider: 'email',
      });
      return { success: true };
    } catch (error) {
      const errorMessages = {
        'auth/user-not-found': 'No account found with this email',
        'auth/wrong-password': 'Incorrect password',
        'auth/invalid-email': 'Invalid email address',
        'auth/too-many-requests': 'Too many attempts. Please try again later',
        'auth/invalid-credential': 'Invalid email or password',
      };
      return {
        success: false,
        error: errorMessages[error.code] || error.message
      };
    }
  };

  /**
   * [PURPOSE]: Email/password signup
   * @param {string} email
   * @param {string} password
   * @param {string} displayName
   * @returns {Object} { success: boolean, error?: string }
   */
  const signup = async (email, password, displayName, tosAccepted = false) => {
    if (!auth) return { success: false, error: 'Firebase not configured' };

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Set display name
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }

      // [S5]: Record ToS consent at signup
      if (tosAccepted && database) {
        const consentRef = ref(database, `users/${userCredential.user.uid}/consent`);
        await set(consentRef, {
          terms: true,
          privacy: true,
          acceptedAt: new Date().toISOString(),
          appVersion: APP_VERSION,
        });
        setRequiresConsent(false);
      }

      // [S6]: Track successful email signup (no PII)
      trackEvent('sign_up', { method: 'email' });
      return { success: true };
    } catch (error) {
      const errorMessages = {
        'auth/email-already-in-use': 'An account with this email already exists',
        'auth/weak-password': 'Password must be at least 6 characters',
        'auth/invalid-email': 'Invalid email address',
      };
      return {
        success: false,
        error: errorMessages[error.code] || error.message
      };
    }
  };

  /**
   * [PURPOSE]: Google Sign-In
   * @returns {Object} { success: boolean, error?: string }
   */
  const loginWithGoogle = async () => {
    if (!auth) return { success: false, error: 'Firebase not configured' };

    try {
      const provider = new GoogleAuthProvider();
      const credential = await signInWithPopup(auth, provider);
      // [S6]: Track successful Google sign-in (no PII)
      trackEvent('sign_in', { method: 'google' });
      // [BATCH 13b / P1 D-33 — APR 2026]: Rule 13.
      systemBus.emit(EVENTS.SIGNED_IN, {
        uid: credential?.user?.uid,
        provider: 'google',
      });
      return { success: true };
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        return { success: false, error: null }; // User cancelled — not an error
      }
      return {
        success: false,
        error: error.message
      };
    }
  };

  /**
   * [PURPOSE]: Sign out + clear user-scoped localStorage so the next user on
   *           the same browser doesn't see the previous user's data.
   * [BATCH 1.5 — S-12]: Pre-fix, logout left character roster, journal, party
   *           IDs, and tutorial flags in localStorage. On a shared device
   *           (family tablet, kiosk) User B saw User A's data on next sign-in.
   * @returns {Object} { success: boolean, error?: string }
   */
  const logout = async () => {
    if (!auth) return { success: false, error: 'Firebase not configured' };

    try {
      const oldUid = auth?.currentUser?.uid || null;
      await signOut(auth);
      // [BATCH 1.5 — S-12]: Clear user-scoped localStorage AFTER signOut so
      // any in-flight saves complete first. Helper swallows individual key
      // errors (storage may be disabled / full).
      clearUserScopedStorage();
      // [S6]: Track sign-out
      trackEvent('sign_out');
      // [BATCH 13b / P1 D-33 — APR 2026]: Rule 13 — emit so the
      //   character activity log captures the sign-out before the
      //   character is unloaded.
      systemBus.emit(EVENTS.SIGNED_OUT, { uid: oldUid });
      return { success: true };
    } catch (error) {
      reportError(error, { area: 'sign_out' });
      return { success: false, error: error.message };
    }
  };

  // ========================================
  // [P2.15] GOOGLE ACCOUNT LINKING
  // ========================================

  /**
   * [PURPOSE]: Link a Google account to the currently signed-in user.
   * [WHY]: Email/password users want to add Google sign-in without creating a
   *        second account. Premium status, characters, and tokens stay on the
   *        same uid; the user just gains a new sign-in path.
   * [REQUIRES]: Google provider must be enabled in Firebase Console
   *             (Authentication → Sign-in method → Google → Enable).
   * @returns {Object} { success: boolean, error?: string }
   */
  const linkGoogleAccount = useCallback(async () => {
    if (!auth || !currentUser) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const provider = new GoogleAuthProvider();
      await linkWithPopup(currentUser, provider);
      trackEvent('google_account_linked');
      return { success: true };
    } catch (error) {
      const errorMessages = {
        'auth/provider-already-linked': 'This account is already linked to Google',
        'auth/credential-already-in-use': 'This Google account is linked to a different user',
        'auth/email-already-in-use': 'This Google account is linked to a different user',
        'auth/popup-closed-by-user': 'Popup closed — try again',
        'auth/cancelled-popup-request': 'Popup closed — try again',
        'auth/popup-blocked': 'Popup blocked by browser — allow popups and try again',
        'auth/operation-not-allowed': 'Google sign-in is not enabled. Contact support.',
      };
      // [PURPOSE]: Report unexpected link failures. Expected codes (popup-closed,
      // already-linked, etc.) still report but get tagged with code for filtering.
      reportError(error, { area: 'link_google', extra: { code: error?.code } });
      return {
        success: false,
        error: errorMessages[error.code] || error.message || 'Failed to link Google account',
      };
    }
  }, [currentUser]);

  /**
   * [PURPOSE]: Unlink the Google provider from the currently signed-in user.
   * [WHY]: Symmetry with linkGoogleAccount — users may want to remove Google
   *        sign-in (e.g., switching primary email). Firebase requires at least
   *        one auth method to remain.
   * @returns {Object} { success: boolean, error?: string }
   */
  const unlinkGoogleAccount = useCallback(async () => {
    if (!auth || !currentUser) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      await unlink(currentUser, 'google.com');
      trackEvent('google_account_unlinked');
      return { success: true };
    } catch (error) {
      const errorMessages = {
        'auth/no-such-provider': 'Google account is not linked',
        'auth/requires-recent-login': 'Please sign in again before unlinking',
      };
      reportError(error, { area: 'unlink_google', extra: { code: error?.code } });
      return {
        success: false,
        error: errorMessages[error.code] || error.message || 'Failed to unlink Google account',
      };
    }
  }, [currentUser]);

  // ========================================
  // [S5] COMPLIANCE METHODS
  // ========================================

  /**
   * [PURPOSE]: Accept Terms of Service and Privacy Policy
   * [WHY]: GDPR/CCPA + Google Play require explicit consent recording
   * [INTEGRATION]: Called from AuthModal (signup) and ConsentPrompt (existing users)
   */
  const acceptTerms = useCallback(async () => {
    if (!currentUser?.uid || !database) return { success: false, error: 'Not authenticated' };
    try {
      const consentRef = ref(database, `users/${currentUser.uid}/consent`);
      await set(consentRef, {
        terms: true,
        privacy: true,
        acceptedAt: new Date().toISOString(),
        appVersion: APP_VERSION,
      });
      setRequiresConsent(false);
      return { success: true };
    } catch (error) {
      reportError(error, { area: 'accept_terms' });
      return { success: false, error: error.message };
    }
  }, [currentUser]);

  /**
   * [PURPOSE]: Export ALL user data as a JSON download
   * [WHY]: GDPR Article 20 — right to data portability; CCPA right to know
   * [INTEGRATION]: Called from Settings → Data Management and before account deletion
   * @returns {Object} { success: boolean, data?: object, error?: string }
   */
  const exportAllUserData = useCallback(async () => {
    try {
      // [GATHER]: Account metadata from Firebase Auth
      const account = {
        email: currentUser?.email || null,
        displayName: currentUser?.displayName || null,
        uid: currentUser?.uid || null,
        createdAt: currentUser?.metadata?.creationTime || null,
        lastSignIn: currentUser?.metadata?.lastSignInTime || null,
        provider: currentUser?.providerData?.[0]?.providerId || null,
      };

      // [GATHER]: Firebase Realtime Database user record
      let firebaseUserData = null;
      if (database && currentUser?.uid) {
        try {
          const userRef = ref(database, `users/${currentUser.uid}`);
          const snapshot = await get(userRef);
          if (snapshot.exists()) {
            firebaseUserData = snapshot.val();
          }
        } catch (e) {
          console.error('Export: failed to read Firebase user data:', e);
        }
      }

      // [GATHER]: localStorage data
      const safeGetItem = (key) => {
        try {
          const raw = localStorage.getItem(key);
          return raw ? JSON.parse(raw) : null;
        } catch {
          return localStorage.getItem(key) || null;
        }
      };

      const exportData = {
        schema_version: 1,
        exported_at: new Date().toISOString(),
        app_version: APP_VERSION,
        account,
        purchase_history: {
          hasPremiumLicense: firebaseUserData?.hasPremiumLicense || false,
          purchaseDate: firebaseUserData?.purchaseDate || null,
          aiTokens: firebaseUserData?.aiTokens || 0,
          totalImagesGenerated: firebaseUserData?.totalImagesGenerated || 0,
          tokenPurchases: firebaseUserData?.tokenPurchases || null,
        },
        consent: firebaseUserData?.consent || null,
        characters: safeGetItem('savedCharacters') || [],
        current_autosave: safeGetItem('tabletopInventory_autosave') || null,
        settings: safeGetItem('tabletopInventory_settings') || null,
        custom_content: {
          sources: safeGetItem('tabletop_sources') || null,
          custom_components: safeGetItem('customComponents') || null,
        },
        party: {
          partyId: localStorage.getItem('partyId') || null,
          partyName: localStorage.getItem('partyName') || null,
        },
        tutorial_progress: safeGetItem('tabletopInventory_tutorialState') || null,
        ui_preferences: {
          recentItems: safeGetItem('tabletopInventory_recentItems') || null,
          favoriteItems: safeGetItem('tabletopInventory_favoriteItems') || null,
          quickMenuPosition: safeGetItem('quickMenuPosition') || null,
          collapsedSpellLevels: safeGetItem('collapsedSpellLevels') || null,
        },
      };

      // [TRIGGER]: Browser download
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tti-my-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return { success: true, data: exportData };
    } catch (error) {
      reportError(error, { area: 'export_user_data' });
      return { success: false, error: error.message };
    }
  }, [currentUser]);

  /**
   * [PURPOSE]: Re-authenticate user before sensitive operations (account deletion)
   * [WHY]: Firebase requires recent authentication for deleteUser
   * @param {string|null} password - Password for email/password users; null for Google popup re-auth
   * @returns {Object} { success: boolean, error?: string }
   */
  const reauthenticate = useCallback(async (password = null) => {
    if (!auth || !currentUser) return { success: false, error: 'Not authenticated' };

    try {
      const providerId = currentUser.providerData?.[0]?.providerId;

      if (providerId === 'google.com') {
        // [GOOGLE]: Re-auth via popup
        const provider = new GoogleAuthProvider();
        await reauthenticateWithPopup(currentUser, provider);
      } else {
        // [EMAIL]: Re-auth with email + password
        if (!password) return { success: false, error: 'Password required for re-authentication' };
        const credential = EmailAuthProvider.credential(currentUser.email, password);
        await reauthenticateWithCredential(currentUser, credential);
      }
      return { success: true };
    } catch (error) {
      const errorMessages = {
        'auth/wrong-password': 'Incorrect password',
        'auth/invalid-credential': 'Invalid credentials',
        'auth/popup-closed-by-user': 'Re-authentication cancelled',
        'auth/too-many-requests': 'Too many attempts. Please try again later',
      };
      return {
        success: false,
        error: errorMessages[error.code] || error.message,
      };
    }
  }, [currentUser]);

  /**
   * [PURPOSE]: Delete user account and all associated data
   * [WHY]: GDPR Article 17 — right to erasure; Google Play Data Safety requirement
   * [INTEGRATION]: Called from Settings → Account → Delete Account
   * [FLOW]:
   *   1. Export all data (download JSON)
   *   2. Delete Firebase Realtime DB user record
   *   3. Clean up party memberships
   *   4. Delete Firebase Auth user
   *   5. Sign out
   * @param {boolean} skipExport - Skip the data export step (if already exported)
   * @returns {Object} { success: boolean, error?: string }
   */
  const deleteAccount = useCallback(async (skipExport = false) => {
    if (!auth || !currentUser) return { success: false, error: 'Not authenticated' };

    const uid = currentUser.uid;

    try {
      // [STEP 1]: Export data before deletion
      if (!skipExport) {
        await exportAllUserData();
      }

      // [STEP 2]: Delete user data from Firebase Realtime Database
      if (database) {
        try {
          await remove(ref(database, `users/${uid}`));
        } catch (e) {
          reportError(e, { area: 'delete_account_db_remove', extra: { uid } });
          // Continue — Auth deletion is more important
        }
      }

      // [STEP 3]: Clean up party memberships
      if (database) {
        const partyId = localStorage.getItem('partyId');
        if (partyId) {
          try {
            // Check if user is DM — if so, delete the whole party
            const metadataRef = ref(database, `parties/${partyId}/metadata`);
            const metaSnap = await get(metadataRef);
            const metadata = metaSnap.exists() ? metaSnap.val() : {};
            const characterId = localStorage.getItem('tabletopInventory_autosave');
            let currentCharId = null;
            try {
              const autosave = JSON.parse(characterId);
              currentCharId = autosave?.playerStats?.id;
            } catch { /* ignore */ }

            if (metadata.dmId === currentCharId || metadata.creatorId === currentCharId) {
              // User is DM/creator — remove entire party
              await remove(ref(database, `parties/${partyId}`));
            } else {
              // User is just a member — remove self
              if (currentCharId) {
                await remove(ref(database, `parties/${partyId}/members/${currentCharId}`));
              }
              // Remove from memberUids
              await remove(ref(database, `parties/${partyId}/memberUids/${uid}`));
            }
          } catch (e) {
            reportError(e, { area: 'delete_account_party_cleanup' });
          }
        }
      }

      // [STEP 4]: Delete Firebase Auth user
      try {
        await deleteUser(currentUser);
      } catch (error) {
        if (error.code === 'auth/requires-recent-login') {
          return { success: false, error: 'requires-recent-login' };
        }
        throw error;
      }

      // [STEP 5]: Clear local storage via the shared helper (S-12 fix).
      // Single source of truth for the user-scoped key list lives at
      // src/utils/userStorageCleanup.js so logout() and deleteAccount()
      // don't drift apart.
      clearUserScopedStorage();

      // [BATCH 13b / P1 D-33 — APR 2026]: Rule 13. Emit on the way
      //   out so subscribers can write a final activity-log entry
      //   before the user's character is unloaded.
      systemBus.emit(EVENTS.ACCOUNT_DELETED, { uid });

      return { success: true };
    } catch (error) {
      reportError(error, { area: 'delete_account' });
      return { success: false, error: error.message };
    }
  }, [currentUser, exportAllUserData]);

  // ========================================
  // CONTEXT VALUE
  // ========================================

  // [FIX APR 2026 — AUDIT C-4]: Memoize context value to prevent unnecessary consumer re-renders
  const value = useMemo(() => ({
    // State
    currentUser,
    isAuthenticated: !!currentUser,
    isPremium: premiumStatus.isPremium,
    aiTokens: premiumStatus.aiTokens,
    premiumStatus,
    loading,
    requiresConsent,

    // Auth methods
    login,
    signup,
    loginWithGoogle,
    logout,

    // [P2.15]: Google account linking
    linkGoogleAccount,
    unlinkGoogleAccount,

    // Premium management
    refreshPremiumStatus,

    // [S5]: Compliance methods
    acceptTerms,
    exportAllUserData,
    deleteAccount,
    reauthenticate,
  }), [currentUser, premiumStatus, loading, requiresConsent, login, signup, loginWithGoogle, logout, linkGoogleAccount, unlinkGoogleAccount, refreshPremiumStatus, acceptTerms, exportAllUserData, deleteAccount, reauthenticate]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
